import React, { useEffect, useState } from 'react';
import { FaExpandAlt, FaTimes, FaChartLine, FaCheckCircle, FaClock, FaMedal, FaGraduationCap } from 'react-icons/fa';
import { generateClient } from 'aws-amplify/api';
import { getCurrentUser } from 'aws-amplify/auth';
import SpecialtyOverview from '../../../data/SpecialtyOverview.json';

const StageProgression = ({
  expandedContainer,
  onExpand,
  onClose,
  isExpandedInCenter,
  selectedSpecialty, // new: specialty to resolve training length
}) => {
  const isExpanded = expandedContainer === 'stageProgression' || isExpandedInCenter;

  // Derived progress state
  const [percent, setPercent] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const MED_SCHOOL_TOTAL = 5;
  const [medSchoolPercent, setMedSchoolPercent] = useState(0);
  const [medSchoolYear, setMedSchoolYear] = useState(1);

  // Helpers to get training length for a specialty
  const getTrainingLength = (name) => {
    if (!name) return null;
    const item = SpecialtyOverview?.specialties?.find(
      s => (s.name || '').toLowerCase() === name.toLowerCase()
    );
    if (!item?.additional_info) return null;
    const info = item.additional_info;
    // Prefer including_core where present, else plain indicative
    return (info.indicative_length_of_training_including_core ?? info.indicative_length_of_training) ?? null;
  };

  useEffect(() => {
    let cancelled = false;
    const client = generateClient();
    const fetchAndCompute = async () => {
      try {
        setLoading(true);
        setError(null);
        const user = await getCurrentUser();
        if (!user) throw new Error('Not authenticated');

        // Fetch profile by owner to get yearInMedSchool
        const profileByOwnerGql = /* GraphQL */ `
          query ProfileByOwner($owner: String!) {
            profileByOwner(owner: $owner) {
              items { id yearInMedSchool }
            }
          }
        `;
        const { data } = await client.graphql({
          query: profileByOwnerGql,
          variables: { owner: user.username },
          authMode: 'userPool'
        });
        const profile = data?.profileByOwner?.items?.[0];
        const yearInMedSchool = Number(profile?.yearInMedSchool) || 1; // 1..5 typically
        setMedSchoolYear(Math.max(1, Math.min(MED_SCHOOL_TOTAL, yearInMedSchool)));
        // FIX: show Year 4/5 as 80% (use year/total, not completed full years)
        const msPct = Math.round(Math.min(100, Math.max(0, (yearInMedSchool / MED_SCHOOL_TOTAL) * 100)));
        setMedSchoolPercent(msPct);

        // Resolve specialty training length for compact view calc
        const trainingLen = getTrainingLength(selectedSpecialty) ?? 7;
        const totalPathYears = MED_SCHOOL_TOTAL + trainingLen;
        // Keep compact overall % based on completed full med school years (unchanged)
        const completedMedSchoolYears = Math.max(0, Math.min(MED_SCHOOL_TOTAL, yearInMedSchool - 1));
        const pct = Math.round(Math.min(100, Math.max(0, (completedMedSchoolYears / totalPathYears) * 100)));
        if (!cancelled) setPercent(pct);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load progress');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchAndCompute();
    return () => { cancelled = true; };
  }, [selectedSpecialty]);

  const renderCompactView = () => (
    <>
      <h4>Stage Progress</h4>
      <div className="progress-preview">
        <FaChartLine className="progress-icon" />
        <div className="progress-bar">
          <div className="progress" style={{ width: loading ? '0%' : `${percent}%` }}></div>
        </div>
        <span className="progress-text">{loading ? 'Calculating…' : `${percent}% Complete`}{error ? ' — ' + error : ''}</span>
      </div>
    </>
  );

  const renderExpandedView = () => {
    const isMedSchoolComplete = medSchoolPercent >= 100;
    return (
      <div className="expanded-content">
        <h2>Training Progress Overview</h2>
        
        <div className="progress-grid">
          {/* Medical School */}
          <div className={`stage-card ${isMedSchoolComplete ? 'completed' : 'current'}`}>
            <div className="stage-header">
              <FaGraduationCap className="stage-icon" />
              <span className="stage-badge">{isMedSchoolComplete ? 'Completed' : 'In Progress'}</span>
            </div>
            <h3>Medical School</h3>
            <div className="stage-progress">
              <div className="progress-bar">
                <div className="progress" style={{ width: loading ? '0%' : `${medSchoolPercent}%` }}></div>
              </div>
              <span className="progress-value">{loading ? '—' : `${medSchoolPercent}%`}</span>
            </div>
            <p className="stage-caption">Year {medSchoolYear} of {MED_SCHOOL_TOTAL}</p>
          </div>

          {/* Foundation Training */}
          <div className={`stage-card ${isMedSchoolComplete ? 'current' : 'upcoming'}`}>
            <div className="stage-header">
              <FaCheckCircle className="stage-icon" />
              <span className="stage-badge">{isMedSchoolComplete ? 'In Progress' : 'Upcoming'}</span>
            </div>
            <h3>Foundation Training</h3>
            <div className="stage-progress">
              <div className="progress-bar">
                <div className="progress" style={{ width: '0%' }}></div>
              </div>
              <span className="progress-value">0%</span>
            </div>
          </div>

          {/* Specialty Training */}
          <div className="stage-card upcoming">
            <div className="stage-header">
              <FaClock className="stage-icon pulse" />
              <span className="stage-badge future">Upcoming</span>
            </div>
            <h3>Specialty Training</h3>
            <div className="stage-progress">
              <div className="progress-bar">
                <div className="progress" style={{ width: '0%' }}></div>
              </div>
              <span className="progress-value">0%</span>
            </div>
          </div>

          {/* Consultant */}
          <div className="stage-card upcoming">
            <div className="stage-header">
              <FaMedal className="stage-icon" />
              <span className="stage-badge future">Upcoming</span>
            </div>
            <h3>Consultant</h3>
            <div className="stage-progress">
              <div className="progress-bar">
                <div className="progress" style={{ width: '0%' }}></div>
              </div>
              <span className="progress-value">0%</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`info-box stage-progression ${isExpandedInCenter ? 'center-expanded' : ''}`}>
      {!isExpandedInCenter && (
         isExpanded ? (
           <button className="control-btn close" onClick={onClose} title="Close">
             <FaTimes size={12} />
           </button>
         ) : (
           <button className="control-btn expand" onClick={() => onExpand('stageProgression')} title="Expand">
             <FaExpandAlt size={12} />
           </button>
         )
       )}

       {isExpandedInCenter ? renderExpandedView() : renderCompactView()}

       <style jsx>{`
         .info-box {
           background: #fff;
           border-radius: 16px;
           overflow: hidden;
         }

         .info-box h4 {
           font-size: 0.875rem;
           font-weight: 600;
           color: #64748b;
           margin: 0;
           padding: 1.5rem 1.5rem 0;
         }

         .progress-preview {
           padding: 1rem 1.5rem 1.5rem;
         }

         .expanded-content {
           padding: 0;
         }

         .expanded-content h2 {
           padding: 2.5rem;
           margin: 0;
           text-align: center;
           color: #1e293b;
           font-size: 1.875rem;
           font-weight: 700;
           letter-spacing: -0.025em;
           border-bottom: 1px solid #e2e8f0;
         }

         .progress-grid {
           display: grid;
           grid-template-columns: repeat(4, 1fr);
           gap: 1.5rem;
           padding: 2.5rem;
         }

         .stage-card {
           background: #fff;
           border: 1px solid #e2e8f0;
           border-radius: 16px;
           padding: 1.5rem;
           transition: all 0.2s ease;
         }

         .stage-card:hover {
           transform: translateY(-2px);
           box-shadow: 0 12px 24px -4px rgba(0, 0, 0, 0.08);
         }

         /* Semantics: completed = green, current = blue, upcoming = grey */
         .stage-card.completed { border-color: #10b981; background: #f0fdf4; }
         .stage-card.current { border-color: #3b82f6; background: #eff6ff; }
         .stage-card.upcoming { border-color: #e2e8f0; background: #f8fafc; }

         .stage-header {
           display: flex;
           align-items: center;
           justify-content: space-between;
           margin-bottom: 1rem;
         }

         .stage-icon {
           font-size: 1.5rem;
           color: #64748b;
         }

         .stage-card.completed .stage-icon { color: #10b981; }
         .stage-card.current .stage-icon { color: #3b82f6; }
         .stage-card.upcoming .stage-icon { color: #94a3b8; }

         .stage-badge {
           padding: 0.25rem 0.75rem;
           border-radius: 1rem;
           font-size: 0.75rem;
           font-weight: 500;
           background: #e2e8f0;
           color: #64748b;
         }

         .stage-badge.current { background: #bfdbfe; color: #1d4ed8; }
         .stage-card.completed .stage-badge { background: #d1fae5; color: #0f766e; }

         .stage-card h3 {
           font-size: 1.125rem;
           font-weight: 600;
           color: #1e293b;
           margin: 0 0 1rem 0;
         }

         .stage-progress {
           display: flex;
           align-items: center;
           gap: 1rem;
           margin-bottom: 1rem;
         }

         .progress-bar {
           flex: 1;
           height: 6px;
           background: #e2e8f0;
           border-radius: 3px;
           overflow: hidden;
         }

         .progress {
           height: 100%;
           background: #3b82f6;
           border-radius: 3px;
           transition: width 1s ease;
         }

         .progress-value {
           font-size: 0.875rem;
           font-weight: 500;
           color: #64748b;
           min-width: 45px;
         }

         .stage-caption {
           margin: 0.25rem 0 0;
           font-size: 0.85rem;
           color: #64748b;
         }

         @media (max-width: 1024px) {
           .progress-grid { grid-template-columns: repeat(2, 1fr); }
         }
         @media (max-width: 640px) {
           .progress-grid { 
             grid-template-columns: 1fr; 
             padding: 1.5rem;
             gap: 1rem;
           }
           .expanded-content h2 {
             padding: 1.5rem;
             font-size: 1.5rem;
           }
         }

         .pulse {
           animation: pulse 2s infinite;
         }

         @keyframes pulse {
           0% {
             transform: scale(1);
             opacity: 1;
           }
           50% {
             transform: scale(1.1);
             opacity: 0.8;
           }
           100% {
             transform: scale(1);
             opacity: 1;
           }
         }

         // Control button styles
         .control-btn {
           position: absolute;
           top: 0.75rem;
           right: 0.75rem;
           padding: 0.5rem;
           border: none;
           background: transparent;
           color: #94a3b8;
           cursor: pointer;
           transition: all 0.2s ease;
           border-radius: 6px;
         }

         .control-btn:hover {
           background: #f1f5f9;
           color: #64748b;
         }
       `}</style>
     </div>
   );
};

export default StageProgression;